import type { ReactNode, SetStateAction } from "react";
import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";

import type { FloatingFrameProps } from "@similarweb/ai-kit";
import { FooterMode, icons, RunAgentProvider } from "@similarweb/ai-kit";

import type { FloatingFrameContextValue } from "app/components/ai-kit/types";

// -------------------
// Context
// -------------------
const FloatingFrameContext = createContext<FloatingFrameContextValue | undefined>(undefined);

export const useFloatingFrameProps = () => {
    const context = useContext(FloatingFrameContext);
    if (!context) {
        throw new Error("useFloatingFrameConfig must be used within a FloatingFrameProvider");
    }
    return context;
};

// -------------------
// Provider with Defaults
// -------------------
const { contentAgentIcon } = icons.icons;

const defaultFrameProps: FloatingFrameProps = {
    config: {
        className: "floating-frame",
        headerProps: {
            icon: contentAgentIcon,
            title: "AI Agent",
            badge: "Trend Analyzer", // TODO: add localization
            topButtons: {
                showClose: true,
                showMinimize: true,
            },
        },
        footerProps: {
            footerMode: FooterMode.None,
        },
        apiCallback: async (_params) => {
            return {
                message: "Default response",
                data: null,
            } as any;
        },
    },
    showThread: false,
    eventCallback: (BaseEvent) => {
        console.log("Default Event Callback");
    },
    children: null,
};

export const FloatingFrameProvider = ({ children }: { children: ReactNode }) => {
    const [frameProps, _setFrameProps] = useState<FloatingFrameProps>(defaultFrameProps);
    const [isVisible, setIsVisible] = useState<boolean>(false);

    const onFramePropsSetRef = useRef<(() => void) | null>(null);

    const setFrameProps = useCallback((value: SetStateAction<FloatingFrameProps>, onComplete?: () => void) => {
        if (onComplete) {
            onFramePropsSetRef.current = onComplete;
        }

        _setFrameProps((prev) => {
            const next = typeof value === "function" ? value(prev) : value;
            return next;
        });
    }, []);

    useEffect(() => {
        if (onFramePropsSetRef.current) {
            onFramePropsSetRef.current();
            onFramePropsSetRef.current = null;
        }
    }, [frameProps]);

    return (
        <RunAgentProvider>
            <FloatingFrameContext.Provider value={{ frameProps, setFrameProps, setIsVisible, isVisible }}>
                {children}
            </FloatingFrameContext.Provider>
        </RunAgentProvider>
    );
};
